import {CommandClause} from "@psdk/frame-father";
import {CText} from "./text";
import {CImage} from "./image";
import {CFont, CRotation} from "../types";
import {InputImage} from "@psdk/frame-imageb";

/**
 * uni-app 全局对象（仅 generateUniCanvasImage 使用，运行时按需访问）
 */
declare const uni: any;

/**
 * 图片形式的文字。
 *
 * 用于打印机内置字库不支持的特殊字符（例如化学式下标 C₁₅H₃₀O₂）：
 * 先在外部把文字绘制成 RGBA 像素（如 uni.createCanvasContext + uni.canvasGetImageData），
 * 构建成 InputImage 后传入，CTextCanvas 会以图片形式打印。
 *
 * 用法：
 * ```ts
 * // 1. 外部 Canvas 生成文字像素
 * const inputImage = new InputImage({ data, width, height });
 * // 2. 加入 CPCL 命令链
 * .text(new CTextCanvas({ x: 10, y: 10, inputImage }))
 * ```
 */
export class CTextCanvas extends CText {
  private readonly inputImage: InputImage;
  private readonly compress: boolean;

  constructor(options: {
    /**
     * 起始横坐标（必选）
     */
    x: number;
    /**
     * 起始纵坐标（必选）
     */
    y: number;
    /**
     * InputImage（必选，外部通过 Canvas 绘制文字后生成）
     */
    inputImage: InputImage;
    /**
     * 是否压缩图片（可选，默认 false）
     */
    compress?: boolean;
  }) {
    super({
      x: options.x,
      y: options.y,
      content: "",
      font: CFont.TSS16,
      rotation: CRotation.ROTATION_0,
      charset: "gbk",
    });

    this.inputImage = options.inputImage;
    this.compress = options.compress ?? false;
  }

  /** 核心：返回 CImage 的命令，将文字以图片形式打印 */
  clause(): CommandClause {
    if (this.inputImage === null || this.inputImage === undefined) {
      throw new Error("CTextCanvas: inputImage 不能为空，请先通过外部 Canvas 生成");
    }

    const cImage = new CImage({
      x: this.x,
      y: this.y,
      image: this.inputImage,
      compress: this.compress,
    });

    return cImage.clause();
  }

  /**
   * 静态工具方法：使用 uni-app Canvas 把文字渲染成 InputImage。
   *
   * 用于打印机内置字库不支持的特殊字符（如化学式下标 C₁₅H₃₀O₂）。
   * 需页面中存在对应的 `<canvas canvas-id="...">` 元素。
   *
   * 用法：
   * ```ts
   * const inputImage = await CTextCanvas.generateUniCanvasImage({
   *   content: "主要成分内容C₁₅H₃₀O₂",
   *   fontSize: 32,
   *   canvasId: "ctextCanvas",
   *   component: this, // uni-app 页面/组件实例
   * });
   * // 加入 CPCL 命令链
   * .text(new CTextCanvas({ x, y, inputImage }))
   * ```
   */
  static generateUniCanvasImage(options: {
    /**
     * 要绘制的文字内容（必选）
     */
    content: string;
    /**
     * 字号（必选，单位 px，如 32）
     */
    fontSize: number;
    /**
     * 页面中 <canvas> 元素的 canvas-id（必选）
     */
    canvasId: string;
    /**
     * uni-app 页面/组件实例（可选，用于在组件内定位 canvas）
     */
    component?: any;
    /**
     * 内边距（可选，默认 2px，对齐 Java CTextCanvas）
     */
    padding?: number;
    /**
     * 画布最大宽（可选，默认 2400。需 ≤ 页面 <canvas> 元素宽度，文字较长时可传入更大的值）
     */
    maxWidth?: number;
    /**
     * 画布最大高（可选，默认 2400。需 ≤ 页面 <canvas> 元素高度）
     */
    maxHeight?: number;
  }): Promise<InputImage> {
    const {content, fontSize, canvasId, component, padding = 2, maxWidth = 2400, maxHeight = 2400} = options;
    const canvasMaxW = maxWidth; // 画布最大宽（需匹配页面 <canvas> 元素尺寸，可传入更大值避免文字被截断）
    const canvasMaxH = maxHeight; // 画布最大高

    return new Promise((resolve, reject) => {
      try {
        const uniAPI: any = typeof uni !== "undefined" ? uni : (globalThis as any).uni;
        if (!uniAPI) {
          reject(new Error("CTextCanvas.generateUniCanvasImage 需在 uni-app 环境运行"));
          return;
        }

        const ctx = uniAPI.createCanvasContext(canvasId, component);
        ctx.setFillStyle('#FFFFFF');
        ctx.fillRect(0, 0, canvasMaxW, canvasMaxH);
        ctx.setFillStyle('#000000');
        ctx.setFontSize(fontSize);

        // 按文字实际尺寸计算图片大小（对齐 Java CTextCanvas 的 measureText + getTextBounds）
        let textWidth = 0;
        try {
          const metrics = ctx.measureText(content);
          textWidth = metrics && metrics.width ? metrics.width : 0;
        } catch (e) {
          textWidth = 0;
        }
        if (!textWidth || textWidth <= 0) {
          textWidth = content.length * fontSize; // 兜底估算
        }
        const canvasW = Math.min(Math.ceil(textWidth) + padding * 2, canvasMaxW);
        const canvasH = Math.min(Math.ceil(fontSize * 1.2) + padding * 2, canvasMaxH);

        // 绘制文字（基线对齐 Java 的 padding + ascent）
        ctx.fillText(content, padding, padding + Math.ceil(fontSize * 0.8));
        ctx.draw(false, () => {
          uniAPI.canvasGetImageData({
            canvasId,
            x: 0,
            y: 0,
            width: canvasW,
            height: canvasH,
            success: (res: any) => {
              resolve(new InputImage({
                data: res.data,
                width: canvasW,
                height: canvasH,
              }));
            },
            fail: (e: any) => reject(e),
          });
        });
      } catch (e) {
        reject(e);
      }
    });
  }
}
